import { useMemo, useState, type CSSProperties, type ReactNode } from "react";

export type PlainIdentityUserDetailItem = {
  key?: string;
  label?: ReactNode;
  value: ReactNode;
};

export type PlainIdentityUserTextProps = {
  userId?: string | number | null;
  username?: string | null;
  fullName?: string | null;
  displayText?: ReactNode;
  fallback?: string;
  detailItems?: PlainIdentityUserDetailItem[];
  detailLabels?: {
    userId?: ReactNode;
    username?: ReactNode;
    fullName?: ReactNode;
  };
  focusable?: boolean;
  className?: string;
  style?: CSSProperties;
};

function normalizeText(value?: string | number | null) {
  return value == null ? "" : String(value).trim();
}

function stringifyNode(value: ReactNode) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "";
}

const triggerStyle: CSSProperties = {
  position: "relative",
  display: "inline-flex",
  minWidth: 0,
  maxWidth: "100%",
  cursor: "help",
};

const tooltipStyle: CSSProperties = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 8px)",
  zIndex: 60,
  minWidth: 220,
  maxWidth: 320,
  padding: "10px 12px",
  border: "1px solid rgba(15, 23, 42, 0.12)",
  borderRadius: 12,
  background: "#ffffff",
  boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
  color: "#112033",
  fontSize: 12,
  lineHeight: 1.5,
  textAlign: "left",
};

const rowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content minmax(0, 1fr)",
  gap: "8px",
  alignItems: "baseline",
};

const labelStyle: CSSProperties = {
  color: "#64748b",
  whiteSpace: "nowrap",
};

const valueStyle: CSSProperties = {
  minWidth: 0,
  overflowWrap: "anywhere",
};

export function PlainIdentityUserText({
  userId,
  username,
  fullName,
  displayText,
  fallback = "-",
  detailItems,
  detailLabels,
  focusable = true,
  className,
  style,
}: PlainIdentityUserTextProps) {
  const [open, setOpen] = useState(false);
  const normalizedUserId = normalizeText(userId);
  const normalizedUsername = normalizeText(username);
  const normalizedFullName = normalizeText(fullName);
  const normalizedFallback = normalizeText(fallback) || "-";
  const display = displayText ?? (normalizedFullName || normalizedUsername || normalizedUserId || normalizedFallback);
  const resolvedDetailItems = useMemo<PlainIdentityUserDetailItem[]>(() => {
    if (detailItems && detailItems.length > 0) {
      return detailItems;
    }
    return [
      ...(normalizedUserId
        ? [{ key: "user-id", label: detailLabels?.userId, value: normalizedUserId }]
        : []),
      ...(normalizedUsername
        ? [{ key: "username", label: detailLabels?.username, value: normalizedUsername }]
        : []),
      ...(normalizedFullName
        ? [{ key: "full-name", label: detailLabels?.fullName, value: normalizedFullName }]
        : []),
    ];
  }, [detailItems, detailLabels?.fullName, detailLabels?.userId, detailLabels?.username, normalizedFullName, normalizedUserId, normalizedUsername]);
  const title = resolvedDetailItems
    .map((item) => {
      const label = stringifyNode(item.label);
      const value = stringifyNode(item.value);
      return label && value ? `${label}: ${value}` : value;
    })
    .filter(Boolean)
    .join("\n");

  if (!resolvedDetailItems.length) {
    return <>{display}</>;
  }

  return (
    <span
      className={className}
      style={{ ...triggerStyle, ...style }}
      title={title || undefined}
      tabIndex={focusable ? 0 : undefined}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {display}
      {open ? (
        <span role="tooltip" style={tooltipStyle}>
          {resolvedDetailItems.map((item, index) => (
            <span key={item.key ?? index} style={rowStyle}>
              <span style={labelStyle}>{item.label}</span>
              <span style={valueStyle}>{item.value}</span>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
}
